import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiCallError, callFunction } from '@/lib/api';
import { ggopHeading } from '@/lib/ggop/ggopView';
import { GgopViewer } from './GgopViewer';

// T2.2 (GGOP-M): "Modtagne genoptræningsplaner". communicate-ggop reads the clinic's
// received GGOP1/2/3 from Communicate and couples each to a patient by CPR inside the
// database; the browser never gets a CPR from the list.

export type GgopListRow = {
  exchangeId: string;
  direction: 'in' | 'out';
  receivedAt: string;
  envelopeIdentifier: string | null;
  letterIdentifier: string | null;
  senderEan: string | null;
  validationOk: boolean;
  planType: string;
  statisticalCode: string;
  senderName: string | null;
  patientName: string | null;
  patientIdKind: 'cpr' | 'alternative' | 'none';
  sentAt: string | null;
  patientId: string | null;
};

export type GgopView = GgopListRow & { xml: string };

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen', dateStyle: 'short', timeStyle: 'short' });
}

export function ReceivedGgopPanel({
  clinicId,
  patientId,
  refreshKey,
  canRevealCpr = true,
}: {
  clinicId: string;
  patientId?: string;
  refreshKey?: number;
  canRevealCpr?: boolean;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<GgopListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<GgopView | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await callFunction<Record<string, string>, { rows: GgopListRow[] }>('communicate-ggop', {
        action: 'list',
        clinic_id: clinicId,
        ...(patientId ? { patient_id: patientId } : {}),
      });
      if (mine === seq.current) setRows(result.rows);
    } catch (err) {
      if (mine === seq.current) {
        setError(err instanceof ApiCallError ? err.message : t('patients.ggop.load_failed', 'Genoptræningsplaner kunne ikke hentes.'));
      }
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [clinicId, patientId, t]);

  useEffect(() => {
    setRows(null);
    setOpenId(null);
    setView(null);
  }, [clinicId, patientId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function open(exchangeId: string) {
    setOpenId(exchangeId);
    setView(null);
    setViewError(null);
    try {
      setView(
        await callFunction<Record<string, string>, GgopView>('communicate-ggop', {
          action: 'view',
          clinic_id: clinicId,
          exchange_id: exchangeId,
        }),
      );
    } catch (err) {
      setViewError(err instanceof ApiCallError ? err.message : t('patients.ggop.view_failed', 'Genoptræningsplanen kunne ikke vises.'));
    }
  }

  // Always rendered (also on the patient's Korrespondance tab), like 'Modtagne bookingsvar':
  // heading plus 'Ingen modtagne genoptræningsplaner.' when there are none.
  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="received-ggop">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('patients.ggop.received_title', 'Modtagne genoptræningsplaner')} (GGOP)</h3>
        <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? t('patients.ggop.refreshing', 'Opdaterer…') : t('patients.ggop.refresh', 'Opdatér')}
        </Button>
      </div>
      {error && <p role="alert" className="text-sm text-ds-red-ink">{error}</p>}
      {rows === null && loading && (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {rows && rows.length === 0 && <p className="text-sm text-ds-muted">{t('patients.ggop.empty', 'Ingen modtagne genoptræningsplaner.')}</p>}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="received-ggop-list">
            <thead className="text-xs text-ds-muted">
              <tr>
                <th className="py-1 pr-2">{t('patients.ggop.col_type', 'Plan')}</th>
                <th className="py-1 pr-2">{t('patients.ggop.col_patient', 'Patient')}</th>
                <th className="py-1 pr-2">{t('patients.ggop.col_sender', 'Afsender')}</th>
                <th className="py-1 pr-2">{t('patients.ggop.col_sent', 'Afsendt')}</th>
                <th className="py-1">{t('patients.ggop.col_received', 'Modtaget')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.exchangeId}
                  className={`cursor-pointer border-t border-ds-line align-top hover:bg-ds-row-hover   ${openId === row.exchangeId ? 'bg-ds-teal-tint ' : ''}`}
                  onClick={() => void open(row.exchangeId)}
                  data-testid="received-ggop-row"
                >
                  <td className="py-1.5 pr-2">
                    <span className="font-medium">{ggopHeading(row.planType)}</span>
                    <span className="ml-1 text-xs text-ds-muted">{row.statisticalCode}</span>
                    {!row.validationOk && (
                      <div className="text-xs text-ds-amber-ink">{t('patients.ggop.xsd_warning', 'XSD-tjek: afvigelse (kun information)')}</div>
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    {row.patientName ?? '–'}
                    {!row.patientId && <div className="text-xs text-ds-amber-ink">{t('patients.ggop.unmatched', 'Ikke knyttet til en patient')}</div>}
                  </td>
                  <td className="py-1.5 pr-2">{row.senderName ?? row.senderEan ?? '–'}</td>
                  <td className="py-1.5 pr-2 whitespace-nowrap">{row.sentAt ?? ''}</td>
                  <td className="py-1.5 whitespace-nowrap">{formatTime(row.receivedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="secondary" onClick={() => { setOpenId(null); setView(null); }}>
              {t('patients.ggop.close', 'Luk')}
            </Button>
          </div>
          {!view && !viewError && <p className="text-sm text-ds-muted">{t('patients.ggop.loading', 'Henter genoptræningsplanen…')}</p>}
          {viewError && <p role="alert" className="text-sm text-ds-red-ink">{viewError}</p>}
          {view && (
            <GgopViewer
              xml={view.xml}
              planType={view.planType}
              patientId={view.patientId}
              patientName={view.patientName}
              canRevealCpr={canRevealCpr}
            />
          )}
        </div>
      )}
    </section>
  );
}
